import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DetectionZone, LaneLine, ScreenLine, Vector2, Vector3 } from "../../models";
import { ImageCoordTranslator } from "../../hooks/useImageCoordTranslator";
import { calculateScreenLineDirections, SvgArrowDefs, SvgShape } from "./SvgShape";

/** Shape types that can be drawn/edited from the camera perspective. Points are facility-frame meters. */
export type PerspectiveShape = DetectionZone | ScreenLine | LaneLine;

/** Reprojected ground points further than this from their source count as unprojectable (behind the camera / past the horizon). */
const ROUNDTRIP_TOLERANCE_METERS = 2;

/**
 * A click is only placeable if projecting its ground solution back lands within this fraction of
 * the image width from the clicked pixel. Clicks at/above the horizon get a synthetic max-range
 * ground point from imageToGroundClamped, and re-projecting such points folds through the
 * distortion polynomial far outside its calibrated radius — the vertex would land wildly
 * off-screen. Valid clicks roundtrip sub-pixel, so this cleanly separates the two.
 */
const CLICK_ROUNDTRIP_TOLERANCE_FRACTION = 0.015;

type PerspectiveCanvasProps = {
    /** Camera snapshot to draw over */
    imageUrl: string;
    /** Pixel dimensions used for the svg viewBox and projections (should match the image aspect) */
    imageWidth: number;
    imageHeight: number;
    /** Facility-meters <-> image-pixels projection for the device whose camera took the image */
    translator: ImageCoordTranslator;
    /** Facility-frame shapes (meters). The canvas projects them into the image per render. */
    shapes: PerspectiveShape[];
    /** Shape currently being drawn — canvas clicks append points to it */
    inProgressShapeId?: string;
    editable?: boolean;
    selectedShapeIds?: ReadonlySet<string>;
    showLabels?: { detectionZones?: boolean; screenLines?: boolean; laneLines?: boolean };
    /** Emits updated shapes in facility meters (point appended, vertex dragged, shape closed) */
    onShapeChanged?: (shape: PerspectiveShape, remove: boolean) => void;
    onShapeClick?: (shapeId: string) => void;
    onBackgroundClick?: () => void;
    onImageLoad?: (dimensions: { width: number; height: number }) => void;
    onImageError?: () => void;
    className?: string;
    /** Extra SVG content rendered in image-pixel space (e.g. target overlays) */
    children?: React.ReactNode;
};

const cloneShapeWithPoints = <T extends PerspectiveShape>(shape: T, points: Vector2[]): T => Object.assign(Object.create(Object.getPrototypeOf(shape)), shape, { points });

/** Screen line in/out directions are derived from geometry — keep them in sync whenever points change. */
const withScreenLineDirections = <T extends PerspectiveShape>(shape: T): T => {
    if (shape.$type !== ScreenLine.$type) return shape;
    const line = shape as unknown as ScreenLine;
    if ((line.points?.length ?? 0) < 2) return shape;
    const directions = calculateScreenLineDirections(line.points![0], line.points![1], line.reverseInOut);
    line.inboundDirection = directions.inbound;
    line.outboundDirection = directions.outbound;
    return shape;
};

const pointDistance = (a: Vector2 | undefined, b: Vector2 | undefined) => Math.hypot((a?.x ?? 0) - (b?.x ?? 0), (a?.y ?? 0) - (b?.y ?? 0));

export const PerspectiveCanvas: React.FC<PerspectiveCanvasProps> = ({ imageUrl, imageWidth, imageHeight, translator, shapes, inProgressShapeId, editable = false, selectedShapeIds, showLabels, onShapeChanged, onShapeClick, onBackgroundClick, onImageLoad, onImageError, className, children }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [cursor, setCursor] = useState<{ px: Vector2; projectable: boolean } | undefined>();
    const [drag, setDrag] = useState<{ shapeId: string; pointIndex: number; px: Vector2; projectable: boolean; lastGoodPx: Vector2 | null } | null>(null);

    // refs so the document-level mouseup handler always sees current values
    const dragRef = useRef(drag);
    dragRef.current = drag;
    const shapesRef = useRef(shapes);
    shapesRef.current = shapes;
    const translatorRef = useRef(translator);
    translatorRef.current = translator;
    const onShapeChangedRef = useRef(onShapeChanged);
    onShapeChangedRef.current = onShapeChanged;

    const closeThreshold = imageWidth * 0.007;

    const inProgressShape = useMemo(() => (inProgressShapeId ? shapes.find((s) => s.id === inProgressShapeId) : undefined), [shapes, inProgressShapeId]);

    /**
     * Project each shape into image pixels and decide whether it is visible from this camera.
     * A shape is hidden when any point fails to project sanely (behind the camera / past the
     * projection range — detected via a ground->pixel->ground roundtrip) or when no point
     * lands anywhere near the image.
     */
    const projectedShapes = useMemo(() => {
        const projectPoint = (p: Vector2) => translator.groundToImagePixels(new Vector3({ x: p.x, y: p.y, z: 0 }));

        return shapes.map((shape) => {
            const groundPoints = shape.points ?? [];
            if (!groundPoints.length) {
                return { shape, pxShape: cloneShapeWithPoints(shape, []), visible: true };
            }

            const pxPoints = groundPoints.map(projectPoint);
            const isInProgress = shape.id === inProgressShapeId;

            let visible = pxPoints.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
            if (visible && !isInProgress) {
                visible = pxPoints.every((px, i) => {
                    const roundtrip = translator.imagePixelsToGround(px);
                    return Math.hypot(roundtrip.x - groundPoints[i].x, roundtrip.y - groundPoints[i].y) < ROUNDTRIP_TOLERANCE_METERS;
                });
                if (visible) {
                    visible = pxPoints.some((p) => p.x >= -0.25 * imageWidth && p.x <= 1.25 * imageWidth && p.y >= -0.25 * imageHeight && p.y <= 1.25 * imageHeight);
                }
            }

            return { shape, pxShape: cloneShapeWithPoints(shape, pxPoints), visible: visible || isInProgress };
        });
    }, [shapes, translator, imageWidth, imageHeight, inProgressShapeId]);

    const projectedById = useMemo(() => {
        const map = new Map<string, { shape: PerspectiveShape; pxShape: PerspectiveShape; visible: boolean }>();
        projectedShapes.forEach((p) => p.shape.id && map.set(p.shape.id, p));
        return map;
    }, [projectedShapes]);

    const hiddenShapes = useMemo(() => projectedShapes.filter((p) => !p.visible), [projectedShapes]);

    /** Apply the live vertex-drag override to a projected shape (closed zones keep first==last in sync). */
    const withDragOverride = useCallback(
        (pxShape: PerspectiveShape): PerspectiveShape => {
            if (!drag || pxShape.id !== drag.shapeId || !pxShape.points?.length) return pxShape;
            const lastIndex = pxShape.points.length - 1;
            const original = shapesRef.current.find((s) => s.id === drag.shapeId);
            const isClosedZone = original?.$type === DetectionZone.$type && (original as any).isComplete?.() === true;
            // Preview the point that will actually be committed, so the handle visibly stops at the
            // projectable limit instead of following the cursor to a position it cannot be dropped at.
            const previewPx = drag.projectable ? drag.px : (drag.lastGoodPx ?? drag.px);
            const points = pxShape.points.map((p, i) => {
                if (i === drag.pointIndex) return previewPx;
                if (isClosedZone && drag.pointIndex === 0 && i === lastIndex) return previewPx;
                if (isClosedZone && drag.pointIndex === lastIndex && i === 0) return previewPx;
                return p;
            });
            return cloneShapeWithPoints(pxShape, points);
        },
        [drag],
    );

    const getPointPx = useCallback(
        (e: { clientX: number; clientY: number }): Vector2 => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect || rect.width === 0 || rect.height === 0) return new Vector2({ x: 0, y: 0 });
            return new Vector2({
                x: ((e.clientX - rect.left) / rect.width) * imageWidth,
                y: ((e.clientY - rect.top) / rect.height) * imageHeight,
            });
        },
        [imageWidth, imageHeight],
    );

    const clampForInProgress = useCallback(
        (px: Vector2): Vector2 => {
            if (!inProgressShape?.id) return px;
            const pxShape = projectedById.get(inProgressShape.id)?.pxShape as any;
            return pxShape?.clampPointIfClosing?.(px, closeThreshold) ?? px;
        },
        [inProgressShape, projectedById, closeThreshold],
    );

    /** True when a pixel's ground solution re-projects back to (about) the same pixel. */
    const isProjectablePixel = useCallback(
        (px: Vector2): boolean => {
            const ground = translator.imagePixelsToGround(px);
            if (!Number.isFinite(ground.x) || !Number.isFinite(ground.y)) return false;
            const reprojected = translator.groundToImagePixels(new Vector3({ x: ground.x, y: ground.y, z: 0 }));
            if (!Number.isFinite(reprojected.x) || !Number.isFinite(reprojected.y)) return false;
            return Math.hypot(reprojected.x - px.x, reprojected.y - px.y) <= imageWidth * CLICK_ROUNDTRIP_TOLERANCE_FRACTION;
        },
        [translator, imageWidth],
    );
    const isProjectablePixelRef = useRef(isProjectablePixel);
    isProjectablePixelRef.current = isProjectablePixel;

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            if (!editable) return;
            if (!inProgressShape) {
                onBackgroundClick?.();
                return;
            }
            e.preventDefault();

            const pxShape = inProgressShape.id ? projectedById.get(inProgressShape.id)?.pxShape : undefined;
            const clamped = clampForInProgress(getPointPx(e));
            const isClosing = !!pxShape?.points?.length && clamped === pxShape.points![0];

            let newPoints: Vector2[];
            if (isClosing) {
                // Close with the exact ground first point so isComplete()'s equality check holds.
                newPoints = [...(inProgressShape.points ?? []), inProgressShape.points![0]];
            } else {
                // Ignore clicks whose ground solution doesn't re-project back to the click
                // (sky/horizon/extreme-corner clicks) — the vertex would land far off-screen.
                if (!isProjectablePixel(clamped)) return;
                const ground = translator.imagePixelsToGround(clamped);
                newPoints = [...(inProgressShape.points ?? []), new Vector2({ x: ground.x, y: ground.y })];
            }

            onShapeChanged?.(withScreenLineDirections(cloneShapeWithPoints(inProgressShape, newPoints)), false);
        },
        [editable, inProgressShape, projectedById, clampForInProgress, isProjectablePixel, getPointPx, translator, onShapeChanged, onBackgroundClick],
    );

    const completeInProgress = useCallback(() => {
        if (!editable || !inProgressShape) return;
        const points = [...(inProgressShape.points ?? [])];
        // A double-click fires two click events first — drop the duplicated trailing point.
        while (points.length >= 2 && pointDistance(points[points.length - 1], points[points.length - 2]) < 0.05) {
            points.pop();
        }

        if (inProgressShape.$type === DetectionZone.$type) {
            if (points.length < 3 || (inProgressShape as any).isComplete?.() === true) return;
            onShapeChanged?.(cloneShapeWithPoints(inProgressShape, [...points, points[0]]), false);
        } else if (inProgressShape.$type === LaneLine.$type) {
            if (points.length < 2) return;
            const completed = cloneShapeWithPoints(inProgressShape, points) as LaneLine;
            (completed as any).isExplicitlyCompleted = true;
            onShapeChanged?.(completed as PerspectiveShape, false);
        }
    }, [editable, inProgressShape, onShapeChanged]);

    const handleAuxClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.button === 1) {
                e.preventDefault();
                completeInProgress();
            }
        },
        [completeInProgress],
    );

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!editable || !inProgressShape) {
                setCursor((current) => (current ? undefined : current));
                return;
            }
            const raw = getPointPx(e);
            const clamped = clampForInProgress(raw);
            // The closing snap reuses the shape's first ground point, so it is always placeable.
            const projectable = clamped !== raw || isProjectablePixel(clamped);
            setCursor({ px: clamped, projectable });
        },
        [editable, inProgressShape, clampForInProgress, isProjectablePixel, getPointPx],
    );

    const handleMouseLeave = useCallback(() => setCursor(undefined), []);

    const handleVertexDrag = useCallback(
        (e: MouseEvent | React.MouseEvent, pxShape: any, pointIndex: number) => {
            if (!editable || !pxShape?.id) return;
            const px = getPointPx(e);
            const projectable = isProjectablePixelRef.current(px);
            const previous = dragRef.current;
            const carried = previous && previous.shapeId === pxShape.id && previous.pointIndex === pointIndex ? previous.lastGoodPx : null;
            const next = { shapeId: pxShape.id, pointIndex, px, projectable, lastGoodPx: projectable ? px : carried };
            // Mirror into the ref immediately rather than waiting for the render that setDrag
            // schedules: the document mouseup below reads dragRef, and a quick drag can release
            // before React re-renders, which silently dropped the edit.
            dragRef.current = next;
            setDrag(next);
        },
        [editable, getPointPx],
    );

    // Commit a vertex drag on mouseup: convert the final pixel to ground meters and emit the updated shape.
    useEffect(() => {
        const handleUp = () => {
            const currentDrag = dragRef.current;
            if (!currentDrag) return;
            setDrag(null);

            const original = shapesRef.current.find((s) => s.id === currentDrag.shapeId);
            if (!original?.points?.length || currentDrag.pointIndex >= original.points.length) return;

            // Released over an unprojectable pixel (at/past the horizon, where imageToGroundClamped
            // returns a synthetic max-range point that re-projects far off-screen): fall back to the
            // last projectable pixel of this drag so the vertex lands short rather than the whole
            // edit being silently discarded. Only bail when the drag never had a valid position.
            const commitPx = isProjectablePixelRef.current(currentDrag.px) ? currentDrag.px : currentDrag.lastGoodPx;
            if (!commitPx) return;

            const ground = translatorRef.current.imagePixelsToGround(commitPx);
            const newPoint = new Vector2({ x: ground.x, y: ground.y });
            const lastIndex = original.points.length - 1;
            const isClosedZone = original.$type === DetectionZone.$type && (original as any).isComplete?.() === true;

            const newPoints = original.points.map((p, i) => {
                if (i === currentDrag.pointIndex) return newPoint;
                if (isClosedZone && currentDrag.pointIndex === 0 && i === lastIndex) return newPoint;
                if (isClosedZone && currentDrag.pointIndex === lastIndex && i === 0) return newPoint;
                return p;
            });

            onShapeChangedRef.current?.(withScreenLineDirections(cloneShapeWithPoints(original, newPoints)), false);
        };

        document.addEventListener("mouseup", handleUp);
        return () => document.removeEventListener("mouseup", handleUp);
    }, []);

    const shouldShowLabel = useCallback(
        (shape: PerspectiveShape) => {
            if (shape.$type === DetectionZone.$type) return showLabels?.detectionZones ?? true;
            if (shape.$type === ScreenLine.$type) return showLabels?.screenLines ?? true;
            return showLabels?.laneLines ?? true;
        },
        [showLabels],
    );

    const allowShapeClicks = editable && !inProgressShape && !!onShapeClick;

    /**
     * SVG paints in document order and a detection zone is a *filled* polygon, so it swallows
     * pointer events anywhere inside its outline — including over a lane line drawn earlier and
     * over that line's vertex handles. Paint filled shapes first, line shapes above them, and the
     * selected shape last so its handles are always reachable.
     */
    const paintOrder = useCallback(
        (shape: PerspectiveShape): number => {
            if (shape.id && selectedShapeIds?.has(shape.id)) return 2;
            return shape.$type === DetectionZone.$type ? 0 : 1;
        },
        [selectedShapeIds],
    );

    return (
        <div ref={containerRef} className={`relative ${className ?? ""}`} onClick={handleClick} onAuxClick={handleAuxClick} onDoubleClick={completeInProgress} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <img className="w-full rounded" src={imageUrl} alt="Camera view" onLoad={(e) => onImageLoad?.({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })} onError={() => onImageError?.()} />
            <svg className="w-full h-full absolute top-0 left-0" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox={`0 0 ${imageWidth} ${imageHeight}`}>
                <SvgArrowDefs />
                <g id="perspectiveShapes">
                    {projectedShapes
                        .filter((p) => p.visible && p.shape.id !== inProgressShapeId)
                        .sort((a, b) => paintOrder(a.shape) - paintOrder(b.shape))
                        .map((p) => {
                            const isSelected = !!p.shape.id && !!selectedShapeIds?.has(p.shape.id);
                            return <SvgShape key={p.shape.id} shape={withDragOverride(p.pxShape)} scaleRatio={translator.scaleRatio} deviceHeading={translator.deviceHeadingDeg} showLabel={shouldShowLabel(p.shape)} showDirectionArrow={true} isSelected={isSelected} onShapeClick={allowShapeClicks ? onShapeClick : undefined} onVertexDrag={editable && isSelected ? handleVertexDrag : undefined} />;
                        })}
                    {children}
                </g>
                {inProgressShape && inProgressShape.id && projectedById.get(inProgressShape.id) && <SvgShape key="current" shape={projectedById.get(inProgressShape.id)!.pxShape} currentPosition={cursor?.px} scaleRatio={translator.scaleRatio} deviceHeading={translator.deviceHeadingDeg} />}
                {cursor && <circle style={{ fillOpacity: 0.7, strokeWidth: 2, fill: cursor.projectable ? "blue" : "#dc2626" }} r={cursor.projectable ? 3 : 5} cx={cursor.px.x} cy={cursor.px.y} />}
                {drag && !drag.projectable && <circle style={{ fillOpacity: 0.7, strokeWidth: 2, fill: "#dc2626", pointerEvents: "none" }} r={5} cx={drag.px.x} cy={drag.px.y} />}
            </svg>

            {!translator.isCalibrated && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                    <span className="bg-white/90 text-gray-800 text-sm font-medium rounded px-3 py-2">Device calibration is missing — shapes can't be projected onto this camera.</span>
                </div>
            )}

            {hiddenShapes.length > 0 && (
                <div className="absolute bottom-2 left-2 rounded-full bg-gray-900/80 px-3 py-1 text-xs font-medium text-white" title={hiddenShapes.map((p) => p.shape.name ?? p.shape.id).join(", ")}>
                    {hiddenShapes.length} shape{hiddenShapes.length === 1 ? "" : "s"} not visible from this camera
                </div>
            )}
        </div>
    );
};
